import { onSensitiveMemoryCacheClear } from './sensitive-cache-lifecycle';

/**
 * One authoritative read shared by every mount of a screen in the current work session.
 *
 * The cache never survives a document and is cleared by the same logout / midnight signal as
 * the other sensitive in-memory projections. Failed reads are not cached, while concurrent
 * callers share one request. A write or an explicit refresh calls `invalidate` / `read(...,
 * true)`, so the cache cannot turn a failed command into a successful-looking screen.
 */
export type SessionReadCache<T> = Readonly<{
  peek(): T | null;
  read(
    readThrough: () => Promise<T>,
    force?: boolean,
    shouldCache?: (value: T) => boolean,
  ): Promise<T>;
  replace(value: T): void;
  invalidate(): void;
}>;

export function createSessionReadCache<T>(): SessionReadCache<T> {
  let cached: T | null = null;
  let hasCachedValue = false;
  let inFlight: Promise<T> | null = null;
  let generation = 0;

  const invalidate = () => {
    generation += 1;
    cached = null;
    hasCachedValue = false;
    inFlight = null;
  };
  onSensitiveMemoryCacheClear(invalidate);

  return {
    peek: () => (hasCachedValue ? cached : null),
    read(readThrough, force = false, shouldCache = () => true) {
      if (!force && hasCachedValue) return Promise.resolve(cached as T);
      if (inFlight) return inFlight;
      const requestGeneration = generation;
      const request = readThrough()
        .then((value) => {
          // A sign-out or midnight boundary may have fired while the transport was in flight.
          // Return to the original caller, but never seed the next account's cache with it.
          if (generation === requestGeneration && shouldCache(value)) {
            cached = value;
            hasCachedValue = true;
          }
          return value;
        })
        .finally(() => {
          if (inFlight === request) inFlight = null;
        });
      inFlight = request;
      return request;
    },
    replace(value) {
      generation += 1;
      inFlight = null;
      cached = value;
      hasCachedValue = true;
    },
    invalidate,
  };
}

export type SessionKeyedReadCache<T> = Readonly<{
  peek(key: string): T | null;
  read(key: string, readThrough: () => Promise<T>, force?: boolean): Promise<T>;
  invalidate(key?: string): void;
}>;

/** A small LRU variant for paged/search reads; it never grows with arbitrary search input. */
export function createSessionKeyedReadCache<T>(maximumEntries = 8): SessionKeyedReadCache<T> {
  const cached = new Map<string, T>();
  const inFlight = new Map<string, Promise<T>>();
  let generation = 0;

  const invalidate = (key?: string) => {
    generation += 1;
    if (key === undefined) {
      cached.clear();
      inFlight.clear();
      return;
    }
    cached.delete(key);
    inFlight.delete(key);
  };
  onSensitiveMemoryCacheClear(() => invalidate());

  return {
    peek(key) {
      if (!cached.has(key)) return null;
      const value = cached.get(key) as T;
      cached.delete(key);
      cached.set(key, value);
      return value;
    },
    read(key, readThrough, force = false) {
      if (!force && cached.has(key)) {
        const value = cached.get(key) as T;
        cached.delete(key);
        cached.set(key, value);
        return Promise.resolve(value);
      }
      const active = inFlight.get(key);
      if (active) return active;
      const requestGeneration = generation;
      const request = readThrough()
        .then((value) => {
          if (generation === requestGeneration) {
            cached.delete(key);
            cached.set(key, value);
            while (cached.size > Math.max(1, maximumEntries)) {
              const oldest = cached.keys().next().value as string | undefined;
              if (oldest === undefined) break;
              cached.delete(oldest);
            }
          }
          return value;
        })
        .finally(() => {
          if (inFlight.get(key) === request) inFlight.delete(key);
        });
      inFlight.set(key, request);
      return request;
    },
    invalidate,
  };
}
